/**
 * Make a Capacitor plugin object safe to put inside a promise.
 *
 * A Capacitor plugin is a Proxy that answers to *every* property by returning
 * a function that dispatches a native call of that name. That includes `then`,
 * which is the one property name JavaScript treats as load-bearing: an object
 * with a callable `then` is a thenable, and the promise machinery unwraps
 * thenables automatically. So
 *
 *     async function get() { return LocalNotifications }   // or: await plugin
 *
 * does not return the plugin. Resolving a promise with it calls
 * `plugin.then(resolve, reject)`, which dispatches a native call named "then"
 * that no plugin implements. The resulting error rejects an *internal* promise
 * — the one the resolution procedure created — so the caller's `await` never
 * settles and its surrounding `try/catch` never runs. It hangs, forever,
 * silently. That is exactly how the rest-timer notification died: the first
 * line of `scheduleRestDone` awaited the plugin and nothing after it ever ran.
 *
 * Hiding `then` makes the object an ordinary value again. Every other property
 * passes straight through, so callers use the plugin exactly as before.
 */
export function unthenable<T>(value: T): T {
  if (value === null || (typeof value !== 'object' && typeof value !== 'function')) {
    return value
  }
  return new Proxy(value as object, {
    get(target, prop, receiver) {
      if (prop === 'then') return undefined
      return Reflect.get(target, prop, receiver)
    },
  }) as T
}
